import { useAssistantSurfaces } from '../components/panels/DashboardAssistantChatDock/hooks/useAssistantSurfaces';
import { useOrganization, useSession, useUser } from '@clerk/clerk-react';
import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import type {
  AssistantEntry,
  AssistantDisplayPayload,
  AssistantThreadSummary,
} from '@charitystack/dashboard-assistant-contracts';
import {
  closeAssistantThread,
  getAssistantThread,
  isDashboardAssistantHistoryEnabled,
  listAssistantThreads,
} from '../api/dashboardAssistantHistory';

export interface DashboardAssistantDockMessage {
  id?: string;
  kind?: 'message' | 'action';
  actionType?: 'search' | 'navigation' | 'context' | 'error' | 'tool';
  role: 'user' | 'assistant';
  content: string;
  timestamp?: string | null;
  sequence?: number;
  status?: 'pending' | 'complete' | 'error';
  turnId?: string;
  runId?: string;
  parentEntryId?: string;
  displayPayload?: AssistantDisplayPayload;
  feedback?: 'up' | 'down' | null;
  metadata?: Record<string, unknown>;
}

export interface DashboardAssistantDockSession {
  id: string;
  title: string;
  /** A launcher prompt, claimed once by the chat panel. */
  initialPrompt?: string;
  conversationId: string | null;
  createdAt: number;
  lastMessageAt?: string | null;
  historyMessages?: DashboardAssistantDockMessage[];
  isClosed?: boolean;
  entryCount?: number;
  messageEntryCount?: number;
  actionEntryCount?: number;
  hasActionOnlyEntries?: boolean;
}

interface DashboardAssistantDockContextValue {
  assistance: ReturnType<typeof useAssistantSurfaces>;
  drafts: Record<string, string>;
  setSessionDraft: (sessionId: string, text: string) => void;
  clearSubmittedDraft: (sessionId: string, submitted: string) => void;
  historyLoading: boolean;
  historyError: string | null;
  retryHistory: () => void;
  resumeAssistant: () => void;
  sessions: DashboardAssistantDockSession[];
  activeSessionId: string | null;
  isDocked: boolean;
  isExpanded: boolean;
  toggleDocked: () => void;
  openAssistant: () => void;
  // Returns the new session id. `expand: false` registers the session (so it
  // shows as a chip in the dock strip) without opening the dock panel.
  // `title` seeds the chip label. Smart Tagging opens via this too — callers
  // mark the returned id in utils/smartTaggingSessions so the dock brands it.
  openNewChat: (options?: { title?: string; expand?: boolean; initialPrompt?: string }) => string;
  claimInitialPrompt: (sessionId: string) => string | null;
  openHistoryConversation: (conversation: {
    id: string;
    title: string;
    lastMessageAt?: string | null;
    messages?: DashboardAssistantDockMessage[];
  }) => void;
  openSession: (sessionId: string) => void;
  closeSession: (
    sessionId: string,
    archivePatch?: Partial<DashboardAssistantDockSession>,
  ) => void;
  minimize: () => void;
  // Hides the dock after handing a proposal to the editor; the next
  // openAssistantOrResume() reopens that chat once instead of a new one (ADM-198).
  minimizeForHandoff: () => void;
  openAssistantOrResume: () => void;
  refreshSession: (sessionId: string) => Promise<void>;
  updateSession: (sessionId: string, patch: Partial<DashboardAssistantDockSession>) => void;
}

const DashboardAssistantDockContext = createContext<DashboardAssistantDockContextValue | null>(
  null,
);

const DASHBOARD_ASSISTANT_DOCKED_STORAGE_KEY =
  'charitystack-dashboard-assistant-docked';

function getInitialDockedState() {
  if (typeof window === 'undefined') {
    return false;
  }

  try {
    return window.localStorage.getItem(DASHBOARD_ASSISTANT_DOCKED_STORAGE_KEY) === 'true';
  } catch {
    return false;
  }
}

function createSession(): DashboardAssistantDockSession {
  const id =
    typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
      ? crypto.randomUUID()
      : `assistant-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;

  return {
    id,
    title: 'New chat',
    conversationId: null,
    createdAt: Date.now(),
  };
}

function normalizeSession(session: DashboardAssistantDockSession): DashboardAssistantDockSession {
  return {
    ...session,
    historyMessages: session.historyMessages?.map((message) => ({
      ...message,
      kind: message.kind || 'message',
    })),
  };
}

function threadSummaryToSession(
  thread: AssistantThreadSummary,
  existing?: DashboardAssistantDockSession,
): DashboardAssistantDockSession {
  return normalizeSession({
    id: thread.threadId,
    title: thread.title,
    conversationId: thread.threadId,
    createdAt: new Date(thread.createdAt).getTime(),
    lastMessageAt: thread.lastEntryAt ?? thread.updatedAt,
    historyMessages: existing?.historyMessages,
    isClosed: thread.status === 'closed',
    entryCount: thread.entryCount,
    messageEntryCount: thread.messageEntryCount,
    actionEntryCount: thread.actionEntryCount,
    hasActionOnlyEntries: thread.hasActionOnlyEntries,
  });
}

function entryToDockMessage(entry: AssistantEntry): DashboardAssistantDockMessage {
  return {
    id: entry.entryId,
    actionType:
      entry.kind === 'action' || entry.kind === 'error'
        ? ((entry.metadata?.actionType as DashboardAssistantDockMessage['actionType']) ||
            (entry.kind === 'error' ? 'error' : 'tool'))
        : undefined,
    content: entry.content,
    feedback:
      entry.metadata && typeof entry.metadata.feedback === 'string'
        ? (entry.metadata.feedback as 'up' | 'down')
        : ((entry as AssistantEntry & { feedback?: 'up' | 'down' }).feedback ?? null),
    kind: entry.kind === 'action' || entry.kind === 'error' ? 'action' : 'message',
    role: entry.role === 'user' ? 'user' : 'assistant',
    timestamp: entry.createdAt,
    sequence: entry.sequence,
    status: entry.status,
    turnId: entry.turnId,
    runId: entry.runId,
    parentEntryId: entry.parentEntryId,
    displayPayload: entry.displayPayload,
    metadata: entry.metadata,
  };
}

function getLocalSessionEntryCounts(messages: DashboardAssistantDockMessage[] = []) {
  return messages.reduce(
    (counts, message) => {
      counts.entryCount += 1;

      if (message.kind === 'action' || message.displayPayload) {
        counts.actionEntryCount += 1;
      } else {
        counts.messageEntryCount += 1;
      }

      return counts;
    },
    {
      actionEntryCount: 0,
      entryCount: 0,
      messageEntryCount: 0,
    },
  );
}

function areHistoryMessagesEqual(
  currentMessages: DashboardAssistantDockMessage[] = [],
  nextMessages: DashboardAssistantDockMessage[] = [],
) {
  if (currentMessages === nextMessages) {
    return true;
  }
  if (currentMessages.length !== nextMessages.length) {
    return false;
  }

  return JSON.stringify(currentMessages) === JSON.stringify(nextMessages);
}

function areSessionsEquivalent(
  currentSession: DashboardAssistantDockSession,
  nextSession: DashboardAssistantDockSession,
) {
  const historyMessagesEqual = areHistoryMessagesEqual(
    currentSession.historyMessages,
    nextSession.historyMessages,
  );

  // `lastMessageAt` is derived from history persistence. A timestamp-only patch
  // with identical messages should not force the dock context to rerender.
  return (
    currentSession.id === nextSession.id &&
    currentSession.title === nextSession.title &&
    currentSession.conversationId === nextSession.conversationId &&
    currentSession.createdAt === nextSession.createdAt &&
    historyMessagesEqual &&
    currentSession.isClosed === nextSession.isClosed &&
    currentSession.entryCount === nextSession.entryCount &&
    currentSession.messageEntryCount === nextSession.messageEntryCount &&
    currentSession.actionEntryCount === nextSession.actionEntryCount &&
    currentSession.hasActionOnlyEntries === nextSession.hasActionOnlyEntries
  );
}

function mergeSessionPatch(
  session: DashboardAssistantDockSession,
  patch: Partial<DashboardAssistantDockSession> = {},
): DashboardAssistantDockSession {
  const mergedSession = {
    ...session,
    ...patch,
  };

  if (!mergedSession.historyMessages?.length) {
    return areSessionsEquivalent(session, mergedSession) ? session : mergedSession;
  }

  const localCounts = getLocalSessionEntryCounts(mergedSession.historyMessages);
  const normalizedSession = {
    ...mergedSession,
    actionEntryCount: Math.max(
      mergedSession.actionEntryCount ?? 0,
      localCounts.actionEntryCount,
    ),
    entryCount: Math.max(mergedSession.entryCount ?? 0, localCounts.entryCount),
    hasActionOnlyEntries:
      mergedSession.hasActionOnlyEntries ||
      (localCounts.actionEntryCount > 0 && localCounts.messageEntryCount === 0),
    messageEntryCount: Math.max(
      mergedSession.messageEntryCount ?? 0,
      localCounts.messageEntryCount,
    ),
  };
  return areSessionsEquivalent(session, normalizedSession) ? session : normalizedSession;
}

function getFallbackNextActiveSessionId(
  sessions: DashboardAssistantDockSession[],
  closingSessionId: string,
  currentActiveSessionId: string | null,
) {
  if (currentActiveSessionId !== closingSessionId) {
    return currentActiveSessionId;
  }

  const remainingSessions = sessions.filter(
    (session) => session.id !== closingSessionId && !session.isClosed,
  );
  return remainingSessions.length > 0 ? remainingSessions[remainingSessions.length - 1].id : null;
}

export function DashboardAssistantDockProvider({ children }: { children: React.ReactNode }) {
  const { isLoaded: isUserLoaded, user } = useUser();
  const { organization, isLoaded: isOrganizationLoaded } = useOrganization();
  const { session } = useSession();
  const userId = user?.id || null;
  const memberships = user?.organizationMemberships || [];
  const singleMembershipOrgId =
    memberships.length === 1 ? memberships[0]?.organization?.id : undefined;
  const organizationId =
    organization?.id || session?.lastActiveOrganizationId || singleMembershipOrgId || null;
  const [sessions, setSessions] = useState<DashboardAssistantDockSession[]>([]);
  const [activeSessionId, setActiveSessionId] = useState<string | null>(null);
  const [isDocked, setIsDocked] = useState(getInitialDockedState);
  const [isExpanded, setIsExpanded] = useState(false);
  const scope = `${userId}:${organizationId}`;
  const scopeRef = useRef(scope);
  scopeRef.current = scope;
  const requestRef = useRef(0);
  const initialPromptsRef = useRef(new Map<string, { scope: string; prompt: string }>());
  const claimInitialPrompt = useCallback((sessionId: string) => {
    if (scopeRef.current !== scope) return null;
    const pending = initialPromptsRef.current.get(sessionId);
    if (!pending || pending.scope !== scope) return null;
    initialPromptsRef.current.delete(sessionId);
    return pending.prompt;
  }, [scope]);
  const sessionRequestEpochsRef = useRef(new Map<string, number>());
  const [draftState, setDraftState] = useState<{ scope: string; values: Record<string, string> }>({ scope, values: {} });
  const drafts = draftState.scope === scope ? draftState.values : {};
  const setSessionDraft = useCallback((sessionId: string, text: string) => {
    if (scopeRef.current !== scope) return;
    const pending = initialPromptsRef.current.get(sessionId);
    if (pending && text !== pending.prompt) initialPromptsRef.current.delete(sessionId);
    setDraftState(current => ({ scope, values: { ...(current.scope === scope ? current.values : {}), [sessionId]: text } }));
  }, [scope]);
  const clearSubmittedDraft = useCallback((sessionId: string, submitted: string) => {
    if (scopeRef.current !== scope) return;
    setDraftState(current => current.scope === scope && current.values[sessionId] === submitted
      ? { ...current, values: { ...current.values, [sessionId]: '' } } : current);
  }, [scope]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState<string | null>(null);
  const [historyAttempt, setHistoryAttempt] = useState(0);
  const retryHistory = useCallback(() => setHistoryAttempt(attempt => attempt + 1), []);
  const hideAI = useCallback(() => { requestRef.current += 1; setIsExpanded(false); }, []);
  const assistance = useAssistantSurfaces(scope, hideAI);
  const { showAI } = assistance;
  const expandAI = useCallback(() => { showAI(); setIsExpanded(true); }, [showAI]);
  const localDraftSessionIdsRef = useRef<Set<string>>(new Set());
  const resumeNextRef = useRef<string | null>(null);
  const remoteHistoryEnabled = isDashboardAssistantHistoryEnabled();

  useEffect(() => {
    requestRef.current += 1;
    setDraftState({ scope, values: {} });
    localDraftSessionIdsRef.current.clear();
    initialPromptsRef.current.clear();
    resumeNextRef.current = null;
    setSessions([]);
    setActiveSessionId(null);
    setIsExpanded(false);
  }, [organizationId, userId, scope]);

  useEffect(() => {
    if (!isUserLoaded || !isOrganizationLoaded || !userId || !organizationId || !remoteHistoryEnabled) {
      return;
    }

    let isCancelled = false;
    setHistoryLoading(true);
    setHistoryError(null);

    listAssistantThreads({ organizationId, limit: 50 })
      .then(({ threads }: { threads: AssistantThreadSummary[] }) => {
        if (isCancelled) {
          return;
        }

        setSessions((currentSessions) => {
          const remoteSessionIds = new Set(threads.map((thread: AssistantThreadSummary) => thread.threadId));
          const remoteSessions = threads.map((thread: AssistantThreadSummary) =>
            threadSummaryToSession(
              thread,
              currentSessions.find((session) => session.id === thread.threadId),
            ),
          );
          const localOnlySessions = currentSessions.filter(
            (session) =>
              !remoteSessionIds.has(session.id) &&
              !session.isClosed &&
              (!session.historyMessages?.length || localDraftSessionIdsRef.current.has(session.id)),
          );
          return [...remoteSessions, ...localOnlySessions];
        });
      })
      .catch((error) => {
        if (isCancelled) return;
        setHistoryError('Previous conversations could not load. Please try again.');
        console.warn('Failed to load dashboard assistant sessions:', error);
      }).finally(() => { if (!isCancelled) setHistoryLoading(false); });

    return () => {
      isCancelled = true;
    };
  }, [isOrganizationLoaded, isUserLoaded, organizationId, remoteHistoryEnabled, userId, historyAttempt]);

  const openNewChat = useCallback((options?: { title?: string; expand?: boolean; initialPrompt?: string }) => {
    requestRef.current += 1;
    resumeNextRef.current = null;
    const base = createSession();
    const initialPrompt = options?.initialPrompt?.trim();
    const session = { ...base, ...(options?.title ? { title: options.title } : {}), ...(initialPrompt ? { initialPrompt } : {}) };
    if (initialPrompt) {
      initialPromptsRef.current.set(session.id, { scope, prompt: initialPrompt });
      setSessionDraft(session.id, initialPrompt);
    }
    localDraftSessionIdsRef.current.add(session.id);
    setSessions((currentSessions) => [...currentSessions, session]);
    setActiveSessionId(session.id);
    if (options?.expand !== false) expandAI();
    else setIsExpanded(false);
    return session.id;
  }, [expandAI, scope, setSessionDraft]);

  const openAssistant = useCallback(() => { openNewChat(); }, [openNewChat]);

  const openSession = useCallback((sessionId: string) => {
    const requestId = ++requestRef.current;
    resumeNextRef.current = null;
    const sessionEpoch = sessionRequestEpochsRef.current.get(sessionId) ?? 0;
    setSessions(current => current.map(item => item.id === sessionId ? { ...item, isClosed: false } : item));
    setActiveSessionId(sessionId);
    expandAI();
    if (remoteHistoryEnabled && organizationId && !localDraftSessionIdsRef.current.has(sessionId)) {
      void getAssistantThread({
        threadId: sessionId,
        organizationId,
        limit: 200,
      })
        .then(({ thread, entries }: { thread: AssistantThreadSummary; entries: AssistantEntry[] }) => {
          if (scopeRef.current !== scope || requestRef.current !== requestId || (sessionRequestEpochsRef.current.get(sessionId) ?? 0) !== sessionEpoch) return;
          const historyMessages = entries.map(entryToDockMessage);
          setSessions((currentSessions) =>
            currentSessions.map((session) =>
              session.id === thread.threadId
                ? {
                    ...threadSummaryToSession(thread),
                    historyMessages,
                    isClosed: false,
                  }
                : session,
            ),
          );
          setActiveSessionId(thread.threadId);
          setIsExpanded(true);
        })
        .catch((error) => {
          if (scopeRef.current !== scope || requestRef.current !== requestId || (sessionRequestEpochsRef.current.get(sessionId) ?? 0) !== sessionEpoch) return;
          console.warn('Failed to refresh dashboard assistant thread:', error);
          setSessions((currentSessions) =>
            currentSessions.map((session) =>
              session.id === sessionId ? { ...session, isClosed: false } : session,
            ),
          );
          setActiveSessionId(sessionId);
          setIsExpanded(true);
        });
      return;
    }

    setSessions((currentSessions) =>
      currentSessions.map((session) =>
        session.id === sessionId ? { ...session, isClosed: false } : session,
      ),
    );
    setActiveSessionId(sessionId);
    setIsExpanded(true);
  }, [organizationId, remoteHistoryEnabled, scope, expandAI]);

  const resumeAssistant = useCallback(() => {
    const current = sessions.find(item => item.id === activeSessionId && !item.isClosed)
      || sessions.find(item => !item.isClosed);
    if (current) openSession(current.id);
    else openNewChat();
  }, [sessions, activeSessionId, openSession, openNewChat]);

  const openHistoryConversation = useCallback(
    (conversation: {
      id: string;
      title: string;
      lastMessageAt?: string | null;
      messages?: DashboardAssistantDockMessage[];
    }) => {
      const requestId = ++requestRef.current;
      resumeNextRef.current = null;
      const sessionEpoch = sessionRequestEpochsRef.current.get(conversation.id) ?? 0;
      expandAI();
      if (remoteHistoryEnabled && organizationId) {
        void getAssistantThread({
          threadId: conversation.id,
          organizationId,
          limit: 200,
        })
          .then(({ thread, entries }: { thread: AssistantThreadSummary; entries: AssistantEntry[] }) => {
            if (scopeRef.current !== scope || requestRef.current !== requestId || (sessionRequestEpochsRef.current.get(conversation.id) ?? 0) !== sessionEpoch) return;
            const historyMessages = entries.map(entryToDockMessage);
            setSessions((currentSessions) => {
              const nextSession = {
                ...threadSummaryToSession(thread),
                historyMessages,
                isClosed: false,
              };
              const hasExisting = currentSessions.some((session) => session.id === thread.threadId);
              setActiveSessionId(thread.threadId);
              setIsExpanded(true);
              return hasExisting
                ? currentSessions.map((session) =>
                    session.id === thread.threadId ? nextSession : session,
                  )
                : [...currentSessions, nextSession];
            });
          })
          .catch((error) => {
            console.warn('Failed to load dashboard assistant thread:', error);
          });
        return;
      }

      setSessions((currentSessions) => {
        const existingSession = currentSessions.find(
          (session) => session.id === conversation.id || session.conversationId === conversation.id,
        );

        if (existingSession) {
          setActiveSessionId(existingSession.id);
          setIsExpanded(true);
          return currentSessions.map((session) =>
            session.id === existingSession.id
              ? {
                  ...session,
                  title: conversation.title || session.title,
                  lastMessageAt: conversation.lastMessageAt ?? session.lastMessageAt,
                  historyMessages: conversation.messages ?? session.historyMessages,
                  isClosed: false,
                }
              : session,
          );
        }

        const session = createSession();
        const historySession: DashboardAssistantDockSession = {
          ...session,
          title: conversation.title || 'Previous chat',
          conversationId: conversation.id,
          lastMessageAt: conversation.lastMessageAt ?? null,
          historyMessages: conversation.messages ?? [],
          isClosed: false,
        };

        setActiveSessionId(historySession.id);
        setIsExpanded(true);
        return [...currentSessions, historySession];
      });
    },
    [organizationId, remoteHistoryEnabled, scope, expandAI],
  );

  const refreshSession = useCallback(
    async (sessionId: string) => {
      if (!remoteHistoryEnabled || !organizationId) {
        return;
      }

      const sessionEpoch = sessionRequestEpochsRef.current.get(sessionId) ?? 0;
      const { thread, entries } = await getAssistantThread({
        threadId: sessionId,
        organizationId,
        limit: 200,
      });
      if (scopeRef.current !== scope || (sessionRequestEpochsRef.current.get(sessionId) ?? 0) !== sessionEpoch) return;
      const historyMessages = entries.map(entryToDockMessage);
      localDraftSessionIdsRef.current.delete(thread.threadId);
      setSessions((currentSessions) => {
        const nextSession = {
          ...threadSummaryToSession(thread),
          historyMessages,
          isClosed: false,
        };
        const hasExisting = currentSessions.some((session) => session.id === thread.threadId);
        return hasExisting
          ? currentSessions.map((session) =>
              session.id === thread.threadId ? nextSession : session,
            )
          : [...currentSessions, nextSession];
      });
    },
    [organizationId, remoteHistoryEnabled, scope],
  );

  const closeSession = useCallback(
    (sessionId: string, archivePatch?: Partial<DashboardAssistantDockSession>) => {
      initialPromptsRef.current.delete(sessionId);
      // Closing invalidates only this conversation, not another tab's load.
      sessionRequestEpochsRef.current.set(sessionId, (sessionRequestEpochsRef.current.get(sessionId) ?? 0) + 1);
      setSessions((currentSessions) => {
        const nextActiveSessionId = getFallbackNextActiveSessionId(
          currentSessions,
          sessionId,
          activeSessionId,
        );
        setActiveSessionId(nextActiveSessionId);
        setIsExpanded(nextActiveSessionId !== null && isExpanded);
        return currentSessions.flatMap((session) => {
          if (session.id !== sessionId) {
            return [session];
          }

          const archivedSession = mergeSessionPatch(session, archivePatch);

          if ((archivedSession.historyMessages?.length ?? 0) > 0 || (archivedSession.entryCount ?? 0) > 0) {
            if (remoteHistoryEnabled && organizationId) {
              void closeAssistantThread({ threadId: sessionId, organizationId }).catch((error) => {
                console.warn('Failed to close dashboard assistant thread:', error);
              });
            }
            return [{ ...archivedSession, isClosed: true }];
          }

          return [];
        });
      });
    },
    [activeSessionId, isExpanded, organizationId, remoteHistoryEnabled],
  );

  const minimize = hideAI;

  const minimizeForHandoff = useCallback(() => {
    resumeNextRef.current = activeSessionId;
    hideAI();
  }, [activeSessionId, hideAI]);

  const openAssistantOrResume = useCallback(() => {
    const resumeId = resumeNextRef.current;
    if (resumeId && sessions.some(item => item.id === resumeId && !item.isClosed)) openSession(resumeId);
    else openNewChat();
  }, [sessions, openSession, openNewChat]);

  const toggleDocked = useCallback(() => {
    setIsDocked((currentIsDocked) => {
      const nextIsDocked = !currentIsDocked;

      try {
        window.localStorage.setItem(
          DASHBOARD_ASSISTANT_DOCKED_STORAGE_KEY,
          String(nextIsDocked),
        );
      } catch {
        // Docking still works for this session when storage is unavailable.
      }

      return nextIsDocked;
    });
  }, []);

  const updateSession = useCallback(
    (sessionId: string, patch: Partial<DashboardAssistantDockSession>) => {
      setSessions((currentSessions) =>
        currentSessions.map((session) =>
          session.id === sessionId ? mergeSessionPatch(session, patch) : session,
        ),
      );
    },
    [],
  );

  const value = useMemo(
    () => ({
      assistance, drafts, setSessionDraft, clearSubmittedDraft, resumeAssistant, historyLoading, historyError, retryHistory,
      sessions,
      activeSessionId,
      isDocked,
      isExpanded,
      toggleDocked,
      openAssistant,
      openNewChat,
      claimInitialPrompt,
      openHistoryConversation,
      openSession,
      closeSession,
      minimize,
      minimizeForHandoff,
      openAssistantOrResume,
      refreshSession,
      updateSession,
    }),
    [
      assistance, drafts, setSessionDraft, clearSubmittedDraft, resumeAssistant, historyLoading, historyError, retryHistory,
      activeSessionId,
      closeSession,
      isDocked,
      isExpanded,
      minimize,
      minimizeForHandoff,
      openAssistant,
      openAssistantOrResume,
      openHistoryConversation,
      openNewChat,
      claimInitialPrompt,
      openSession,
      refreshSession,
      sessions,
      toggleDocked,
      updateSession,
    ],
  );

  return (
    <DashboardAssistantDockContext.Provider value={value}>
      {children}
    </DashboardAssistantDockContext.Provider>
  );
}

export function useDashboardAssistantDock() {
  const context = useContext(DashboardAssistantDockContext);
  if (!context) {
    throw new Error(
      'useDashboardAssistantDock must be used within DashboardAssistantDockProvider',
    );
  }
  return context;
}
